import { useState, Fragment } from 'react'

import { useRouter } from 'next/router'

import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Icon from 'src/@core/components/icon'
import { useAuth } from 'src/hooks/useAuth'
import { getImageSrc } from 'src/utils/imageSrc'

const BadgeContentSpan = styled('span')(({ theme }) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 1.5px ${theme.palette.background.paper}`
}))

const UserDropdown = props => {
  const { settings } = props

  const [anchorEl, setAnchorEl] = useState(null)

  const router = useRouter()

  const { logout } = useAuth()

  const auth = useAuth()

  // ** Vars
  const { direction } = settings

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileClick = () => {
    router.push('/user/profile')
    handleDropdownClose()
  }

  const handleDropdownClose = url => {
    if (url) {
      router.push(url)
    }
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    handleDropdownClose()
  }
  const getInitials = name => {
    if (!name) {
      return 'NA'
    }

    const words = name.trim().split(' ')

    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }

    return words
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }
  return (
    <Fragment>
      <Badge
        overlap='circular'
        onClick={handleDropdownOpen}
        sx={{ ml: 2, cursor: 'pointer' }}
        badgeContent={<BadgeContentSpan />}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Avatar
          alt={auth?.user?.name || 'User Avatar'}
          onClick={handleDropdownOpen}
          src={getImageSrc(auth?.user?.image)}
          sx={{
            width: { xs: 28, sm: 35 },
            height: { xs: 28, sm: 35 },
            bgcolor: '#4A90E2',
            color: 'white',
            fontWeight: 400,
            fontSize: { xs: '0.7rem', sm: '0.85rem' }
          }}
        >
          {getInitials(auth?.user?.name)}
        </Avatar>
      </Badge>
      <Menu
        disableRestoreFocus
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{
          '& .MuiMenu-paper': {
            width: 260,
            mt: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: direction === 'ltr' ? 14 : 'auto',
              left: direction === 'rtl' ? 14 : 'auto',
              width: 12,
              height: 12,
              background: '#1B205C',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          },
          '& .MuiList-root': {
            pt: 0,
            pb: 0
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
      >
        <Box sx={{ pt: 3, pb: 2, px: 3, borderRadius: '12px 12px 0 0', background: '#1B205C' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              alt={auth?.user?.name || 'User Avatar'}
              sx={{
                width: '3rem',
                height: '3rem',
                bgcolor: 'white',
                color: '#2B388F',
                fontWeight: 700,
                fontSize: '1.2rem',
                border: '3px solid rgba(255,255,255,0.3)'
              }}
              src={getImageSrc(auth?.user?.image)}
            >
              {getInitials(auth?.user?.name)}
            </Avatar>
            <Box sx={{ display: 'flex', ml: 3, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}> {auth.user.name} </Typography>
              <Typography variant='body2' sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
                {auth.user.role}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ py: 2 }}>
          <MenuItem
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
            onClick={handleProfileClick}
          >
            <Icon icon='mdi:account-outline' style={{ marginRight: '12px', fontSize: '1.25rem' }} />
            <Typography variant='body2'>Profile</Typography>
          </MenuItem>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ pb: 2 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              mx: 2,
              borderRadius: '8px',
              color: '#f44336',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <Icon icon='mdi:logout-variant' style={{ marginRight: '12px', fontSize: '1.25rem' }} />
            <Typography variant='body2'>Logout</Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Fragment>
  )
}

export default UserDropdown
